import type { ErpInvoice } from "@/types/admin";

interface InvoicePrintModalProps {
  invoice: ErpInvoice;
  onClose: () => void;
}

const inr = (n: number, currency = "INR") => {
  const symbol = currency === "USD" ? "$" : currency === "EUR" ? "€" : "₹";
  return `${symbol}${n.toLocaleString(currency === "INR" ? "en-IN" : "en-US")}`;
};

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

/** Full-screen printable invoice document with a Print button. */
export default function InvoicePrintModal({ invoice, onClose }: InvoicePrintModalProps) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-foreground-950/60 p-4">
      {/* Toolbar — hidden when printing */}
      <div className="invoice-print-hide mx-auto mb-4 flex max-w-3xl items-center justify-between gap-3 rounded-xl border border-background-200 bg-background-50 px-5 py-3">
        <div className="flex items-center gap-2">
          <i className="ri-file-text-line text-lg text-primary-600" />
          <span className="text-sm font-semibold text-foreground-900">Invoice preview — {invoice.invoiceNo}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-printer-line text-base" /> Print / Save PDF
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
          >
            <i className="ri-close-line text-base" /> Close
          </button>
        </div>
      </div>

      {/* Printable document */}
      <div className="invoice-print-root mx-auto max-w-3xl rounded-xl border border-background-200 bg-white p-10 text-foreground-900">
        <div className="flex flex-wrap items-start justify-between gap-6 border-b border-background-200 pb-6">
          <div>
            <div className="flex items-center gap-2.5">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className="ri-map-pin-2-fill text-xl" />
              </div>
              <div>
                <p className="font-heading text-lg font-extrabold tracking-tight text-foreground-950">Coastal Bizz Connect</p>
                <p className="text-xs text-foreground-500">The coastal directory, together.</p>
              </div>
            </div>
            <p className="mt-4 text-xs leading-relaxed text-foreground-600">
              Coastal Bizz Connect<br />
              Mangaluru, Karnataka 575001<br />
              support@coastalbizz.in
            </p>
          </div>
          <div className="text-right">
            <p className="font-heading text-3xl font-extrabold tracking-tight text-foreground-950">INVOICE</p>
            <p className="mt-2 text-sm font-semibold text-foreground-800">{invoice.invoiceNo}</p>
            <p className="mt-1 text-xs text-foreground-500">Issued {fmtDate(invoice.issuedAt)}</p>
            <p className="text-xs text-foreground-500">Due {fmtDate(invoice.dueAt)}</p>
            <span className="mt-3 inline-flex rounded-full bg-background-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground-700">
              {invoice.status}
            </span>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-6">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Billed to</p>
            <p className="mt-1.5 font-semibold text-foreground-900">{invoice.customer}</p>
            {invoice.business && <p className="text-sm text-foreground-600">{invoice.business}</p>}
            {invoice.customerEmail && <p className="text-sm text-foreground-600">{invoice.customerEmail}</p>}
          </div>
          {invoice.orderNo && (
            <div className="text-right">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Linked order</p>
              <p className="mt-1.5 font-semibold text-foreground-900">{invoice.orderNo}</p>
            </div>
          )}
        </div>

        <table className="mt-8 w-full border-collapse">
          <thead>
            <tr className="border-b border-background-300">
              <th className="py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Description</th>
              <th className="py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-background-100">
              <td className="py-3.5 text-sm">
                <p className="font-medium text-foreground-900">{invoice.plan || "Listing plan subscription"}</p>
                <p className="text-xs text-foreground-500">Listing plan — {invoice.business || invoice.customer}</p>
              </td>
              <td className="py-3.5 text-right text-sm tabular-nums text-foreground-800">
                {inr(invoice.amount, invoice.currency)}
              </td>
            </tr>
          </tbody>
        </table>

        <div className="mt-6 flex justify-end">
          <div className="w-full max-w-xs space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-foreground-600">Subtotal</span>
              <span className="tabular-nums text-foreground-800">{inr(invoice.amount, invoice.currency)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-foreground-600">Tax (18%)</span>
              <span className="tabular-nums text-foreground-800">{inr(invoice.tax, invoice.currency)}</span>
            </div>
            <div className="flex items-center justify-between border-t border-background-300 pt-2.5">
              <span className="text-sm font-bold uppercase tracking-wide text-foreground-900">Total due</span>
              <span className="font-heading text-lg font-extrabold tabular-nums text-foreground-950">
                {inr(invoice.total, invoice.currency)}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-10 border-t border-background-200 pt-5">
          <p className="text-xs leading-relaxed text-foreground-500">
            Thank you for your business. Payment is due by {fmtDate(invoice.dueAt)}. For any billing questions,
            contact support@coastalbizz.in. This is a computer-generated invoice.
          </p>
        </div>
      </div>
    </div>
  );
}